import type { CityConfig } from "../city-template/types";

export const mountWashingtonConfig: CityConfig = {
  city: "Mount Washington",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "mount-washington",
  route: "/cities/mount-washington",
  title: "Emergency Passport Mount Washington KY | Expedited Passport | Bullitt County",
  metaDescription: "Emergency passport and expedited passport services in Mount Washington, KY — as fast as 24 hours. Fast passport renewal for Bullitt County, Shepherdsville, and the Louisville South corridor. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Mount Washington, KY",
  heroSubtitle: "Need an emergency passport fast from Bullitt County? Fast Passport Center helps Mount Washington and Louisville South travelers get expedited passports as quickly as 24 hours. Serving the Bullitt County area, Shepherdsville, and the southern Jefferson County corridor. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Mount Washington Kentucky suburban community with rolling green hills and residential neighborhoods, small town Kentucky charm with modern homes, clean panoramic view of Bullitt County Kentucky area, bright daytime photography, no visible text or people",
  heroImageAlt: "Mount Washington Kentucky suburban community with rolling green hills and residential neighborhoods",
  geoPosition: "38.0501;-85.5452",
  geoPlacename: "Mount Washington, Kentucky",
  lastModified: "2026-05-29",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 275 miles from Mount Washington via I-65",
  },
  nearbyCitiesZips: [
    { name: "Mount Washington", zips: "40047" },
    { name: "Shepherdsville", zips: "40165, 40166" },
    { name: "Hillview", zips: "40129" },
    { name: "Brooks", zips: "40109" },
    { name: "Lebanon Junction", zips: "40150" },
    { name: "Pioneer Village", zips: "40165" },
    { name: "Fox Chase", zips: "40165" },
    { name: "Zoneton", zips: "40165" },
    { name: "Bullitt County", zips: "40165, 40129, 40109, 40150" },
    { name: "Okolona (nearby)", zips: "40219, 40229" },
    { name: "Fairdale (nearby)", zips: "40118" },
    { name: "Louisville South", zips: "40214, 40215, 40272" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for urgent Bullitt County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Mount Washington case" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels from any UPS in Bullitt County" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Louisville", route: "/cities/louisville" },
    { name: "Expedited passport renewal in Elizabethtown", route: "/cities/elizabethtown" },
    { name: "Fast passport services in Shepherdsville", route: "/shepherdsville" },
    { name: "Same-day passport help in Shively", route: "/cities/shively" },
    { name: "Urgent passport services in Bowling Green", route: "/cities/bowling-green" },
    { name: "Emergency passport help in Frankfort", route: "/cities/frankfort" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Mount Washington remotely via secure courier. We do not have a walk-in office in Bullitt County — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Bullitt County travelers.",
  fpcOfficeDistance: "Serving all of Mount Washington, Shepherdsville, Hillview, and the Bullitt County corridor",
  serviceAreaIntro: "From the heart of Mount Washington to Shepherdsville, from Hillview to Lebanon Junction — Fast Passport Center serves the entire Bullitt County and Louisville South area.",
  nearbyCitiesIntro: "We serve all Mount Washington neighborhoods and nearby communities including Shepherdsville, Hillview, Brooks, Lebanon Junction, Pioneer Village, Fox Chase, Zoneton, and the greater Bullitt County area. If you are in Louisville South or southern Jefferson County, we also have you covered.",
  howItWorksIntro: "Most Mount Washington travelers complete everything online and ship documents overnight from the nearest UPS. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Bullitt County travelers often underestimate how long passport processing takes. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Mount Washington and Bullitt County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Mount Washington? Here are the most common ones we hear from Bullitt County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Medical crisis documentation required",
        "Long drive to Chicago — over 4 hours from Mount Washington",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission slots",
        "Predictable turnaround for Bullitt County travelers",
        "Ideal for upcoming business or leisure travel",
        "Full document review included",
        "Dedicated agent assigned to your Mount Washington case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service mail options",
        "Good for planned travel in the next few months",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Cannot upgrade speed after submission",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Bullitt County travelers with immediate departure needs — as fast as 24 hours for urgent Louisville South international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Mount Washington adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with local acceptance clerk support for Mount Washington applicants.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency replacement for Mount Washington residents with DS-64 form guidance and police documentation support.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors with parental consent and acceptance clerk support near Mount Washington.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes for Mount Washington residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Mount Washington", description: "Urgent 24-hour passport processing for Mount Washington and Bullitt County travelers with immediate international departure needs." },
    { name: "Passport Renewal Mount Washington", description: "Fast passport renewal for Mount Washington residents with passport issued in last 15 years at age 16 or older." },
    { name: "New Passport Mount Washington", description: "First-time passport application assistance for Mount Washington and Bullitt County residents." },
    { name: "Lost Passport Replacement Mount Washington", description: "Emergency passport replacement for lost or stolen passports in Bullitt County." },
    { name: "Child Passport Mount Washington", description: "Passport services for minors in Mount Washington with parental consent and acceptance clerk support." },
    { name: "Name Change Passport Mount Washington", description: "Passport name change after marriage, divorce, or legal name changes for Mount Washington residents." },
  ],
  testimonials: [
    {
      name: "Kevin H.",
      location: "Mount Washington, KY",
      rating: 5,
      text: "Our family cruise to the Bahamas was 8 days away and my wife's passport was expired. I was worried living in Mount Washington would slow things down. Fast Passport Center renewed it in 4 days and shipped to our door. We made the cruise!",
      service: "Passport Renewal",
    },
    {
      name: "Michelle D.",
      location: "Shepherdsville, KY",
      rating: 5,
      text: "Emergency business trip to Mexico for my manufacturing company. Passport was missing. Fast Passport Center processed a replacement in 36 hours. I was on that flight. Thank you for saving my business trip.",
      service: "Lost Passport",
    },
    {
      name: "Rachel P.",
      location: "Mount Washington, KY",
      rating: 5,
      text: "First-time passport for my study abroad program in Italy. I was nervous about the whole process. Fast Passport Center walked me through every step, found an acceptance facility nearby, and my passport came in 5 days. Highly recommend.",
      service: "New Passport",
    },
    {
      name: "James W.",
      location: "Hillview, KY",
      rating: 5,
      text: "Retired and finally traveling to Ireland. My passport had expired years ago. Fast Passport Center renewed it in 5 days and I was on my first international trip. Great service for Bullitt County residents like me.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "mount-washington-1",
      name: "Mount Washington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1085 W Brumley Rd",
      city: "Mount Washington",
      state: "KY",
      zip: "40047",
      phone: "(502) 538-1431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Main post office for Mount Washington residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Mount Washington location. Appointments strongly recommended. Full passport acceptance services.",
      icon: "ri-mail-line",
    },
    {
      id: "mount-washington-2",
      name: "Shepherdsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1050 W Hwy 44",
      city: "Shepherdsville",
      state: "KY",
      zip: "40165",
      phone: "(502) 543-1431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 8 miles from Mount Washington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Shepherdsville and surrounding Bullitt County. Good alternative for Mount Washington residents.",
      icon: "ri-mail-line",
    },
    {
      id: "mount-washington-3",
      name: "Jefferson County Clerk — South Office",
      type: "County Clerk Passport Acceptance",
      address: "3820 Valley Station Rd",
      city: "Louisville",
      state: "KY",
      zip: "40272",
      phone: "(502) 574-5130",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Approximately 12 miles from Mount Washington",
      website: "https://jeffersonclerk.com/",
      websiteLabel: "Jefferson County Clerk",
      notes: "South Louisville office convenient for Bullitt County residents crossing into Jefferson County.",
      icon: "ri-government-line",
    },
    {
      id: "mount-washington-4",
      name: "Okolona Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7701 Preston Hwy",
      city: "Louisville",
      state: "KY",
      zip: "40219",
      phone: "(502) 968-1521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 15 miles from Mount Washington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "South Louisville alternative for Bullitt County residents. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "mount-washington-5",
      name: "Fairdale Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4010 Fairdale Rd",
      city: "Louisville",
      state: "KY",
      zip: "40118",
      phone: "(502) 937-1521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 14 miles from Mount Washington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southwestern Louisville and is accessible for Bullitt County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "mount-washington-6",
      name: "Shively Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3620 Dixie Hwy",
      city: "Louisville",
      state: "KY",
      zip: "40216",
      phone: "(502) 447-1521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 16 miles from Mount Washington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for Bullitt County residents with Louisville West connections.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited", fpc: "No — we handle everything" },
    { feature: "Processing Speed", agency: "Same-day only for emergencies", fpc: "24–48 hours for emergency" },
    { feature: "Document Review", agency: "No — rejections sent back", fpc: "Yes — full review before submission" },
    { feature: "Travel Required", agency: "Yes — long drive to Chicago", fpc: "No — ship from home or Bullitt County" },
    { feature: "Agent Support", agency: "Government clerk, no follow-up", fpc: "Yes — dedicated agent" },
    { feature: "Status Updates", agency: "No tracking provided", fpc: "Daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Incorrect Photo Dimensions",
      description: "Many Bullitt County photo shops don't know the exact 2×2 inch passport requirement. Photos must have a plain white or off-white background — no patterns, no glasses.",
    },
    {
      icon: "ri-file-list-line",
      title: "Pre-Signed DS-11 Form",
      description: "The number one reason for rejection at Mount Washington area acceptance facilities. The clerk must witness you sign the DS-11 in person.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Fee Payment",
      description: "Passport fees change frequently. Mount Washington applicants often arrive with outdated checks. We provide the exact current fee breakdown.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Travel Documentation",
      description: "Without confirmed flight details, the Chicago agency won't grant an emergency appointment. Always have your booking ready.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "Both parents must appear with a minor or submit notarized DS-3053. Many Bullitt County families get caught off guard at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long",
      description: "Standard processing takes 8–11 weeks. Many Bullitt County travelers assume they have time. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes from anywhere in Mount Washington or Bullitt County." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We email you a prepaid overnight label. Drop your documents at any UPS location in Bullitt County or Louisville South." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the U.S. Department of State." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a State Department facility using our registered courier slots — no postal delays." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport to your Mount Washington or Bullitt County address — fully trackable." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Mount Washington, KY?", a: "Yes. Fast Passport Center provides emergency passport services for Mount Washington and Bullitt County travelers. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the long drive to Chicago." },
    { q: "How fast can I renew my passport in Mount Washington?", a: "With Fast Passport Center, Mount Washington travelers can get a passport renewed in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day option is the most popular. Both options include document review and a dedicated agent." },
    { q: "Where is the nearest passport acceptance facility to Mount Washington?", a: "The most convenient options for Mount Washington residents are the Mount Washington Post Office (1085 W Brumley Rd), the Shepherdsville Post Office (1050 W Hwy 44), and the Jefferson County Clerk — South Office (3820 Valley Station Rd). All require appointments — we can help you find availability." },
    { q: "Do I need to drive to Chicago for an expedited passport from Mount Washington?", a: "No. With Fast Passport Center, you complete everything online and ship your documents via prepaid overnight label. Our registered couriers handle the in-person delivery — you stay in Bullitt County and we do the rest." },
    { q: "Can I get a same-day passport from Mount Washington?", a: "Same-day passports from the Chicago Regional Passport Agency are only for life-or-death emergencies — and require proof of travel within 72 hours plus a long drive. For nearly all Mount Washington urgent travel needs, our 24–48 hour emergency courier service is the best option." },
    { q: "How do I get a child passport in Mount Washington, KY?", a: "For child passports in Mount Washington, both parents must appear at an acceptance facility or submit notarized DS-3053 consent. We walk Bullitt County families through the entire process, including appointment scheduling." },
    { q: "What if I lost my passport before a trip from Mount Washington?", a: "Contact Fast Passport Center immediately. We provide lost passport replacement services for Mount Washington residents with DS-64 form guidance and emergency courier processing. We can get your replacement in 24–48 hours." },
    { q: "How much does expedited passport service cost in Mount Washington?", a: "Government fees are fixed: $130 for an adult passport book plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote based on your timeline." },
    { q: "What documents do I need for a first-time passport in Mount Washington?", a: "You need proof of citizenship (birth certificate), government-issued photo ID, two passport photos, and payment. First-time applicants must visit an acceptance clerk in person. We provide a full checklist and review your documents beforehand." },
    { q: "Is there a passport photo service near Mount Washington?", a: "Yes — CVS in Shepherdsville, Walgreens on Hwy 44, and Walmart in Mt. Washington offer passport photos. We review your photos before submission to ensure they meet State Department standards." },
    { q: "Can Mount Washington business travelers use your service?", a: "Absolutely. Many Bullitt County residents work along the I-65 corridor and travel internationally for business. We specialize in fast turnaround that accommodates professional schedules — complete everything online, ship overnight, receive your passport at your door." },
    { q: "How long does passport renewal take for Mount Washington residents?", a: "With Fast Passport Center, Mount Washington passport renewal takes 3–5 business days with our standard expedited option, or 24–48 hours with emergency processing. Standard government mail takes 8–11 weeks — not reliable for most Bullitt County travel timelines." },
    { q: "What is the fastest passport service in Mount Washington, KY?", a: "The fastest option is Fast Passport Center's 24–48 hour emergency courier service. We hand-deliver your documents directly to a U.S. Department of State processing facility, bypassing mail queues and postal delays." },
    { q: "Can I use your service if my passport expires soon before a cruise?", a: "Yes. If your passport is expiring soon, contact us immediately. Cruise lines require your passport to be valid at least 6 months past your return date. We can expedite renewal for Mount Washington residents in 24–48 hours." },
    { q: "What areas near Mount Washington do you serve?", a: "We serve all of Mount Washington and the surrounding Bullitt County communities including Shepherdsville, Hillview, Brooks, Lebanon Junction, Pioneer Village, Fox Chase, Zoneton, and the Louisville South corridor." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Shepherdsville", address: "1955 Hwy 44 E, Shepherdsville, KY 40165", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Shepherdsville", address: "136 Hwy 44 E, Shepherdsville, KY 40165", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Mt. Washington", address: "1030 E Hwy 44, Mt. Washington, KY 40047", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Hillview", address: "1110 Hillview Blvd, Louisville, KY 40129", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Okolona", address: "7701 Preston Hwy, Louisville, KY 40219", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Valley Station", address: "7100 Raggard Rd, Louisville, KY 40216", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Bullitt County Circuit Court Clerk",
      address: "250 Country Ln, Shepherdsville, KY 40165",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Main clerk for Bullitt County. Handles REAL ID and driver's license renewals. Appointment recommended.",
      featured: true,
    },
    {
      name: "Jefferson County Clerk — South Office",
      address: "3820 Valley Station Rd, Louisville, KY 40272",
      hours: "Mon–Fri: 8AM–4:00PM",
      note: "South office for Jefferson County residents in the Louisville South corridor.",
      featured: true,
    },
    {
      name: "Jefferson County Clerk — Main Office",
      address: "527 W Jefferson St, Louisville, KY 40202",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Main clerk for Jefferson County. Full REAL ID services.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "25 miles from Mount Washington",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "The primary airport for Bullitt County travelers. Domestic flights with connections to major international hubs. Approximately 30 minutes from Mount Washington via I-65.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: true,
    },
    {
      code: "LEX",
      name: "Blue Grass Airport (Lexington)",
      distance: "95 miles from Mount Washington",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "Alternative regional airport about 1.5 hours from Mount Washington via I-64.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Blue Grass Airport",
      featured: false,
    },
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "120 miles from Mount Washington",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "Major international hub for long-haul flights. Accessible via I-71 from Mount Washington.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: false,
    },
  ],
};